import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Mail, Phone, RefreshCw, Download, Search } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aurelume_admin_token";

const authHeaders = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  const input =
    "w-full bg-white border border-[#E8E4D9] px-5 py-4 outline-none focus:border-[#C5A059] transition-colors";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F3EFE6] px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-md bg-[#FDFBF7] border border-[#E8E4D9] p-10">
        <div className="flex items-center gap-3 text-[#C5A059] mb-8">
          <Logo size={40} />
          <div>
            <p className="font-serif text-xl tracking-[0.15em] text-[#1A1713]">AURELUME</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#78716C]">Admin Dashboard</p>
          </div>
        </div>
        <label className="block text-sm text-[#78716C] mb-2">Email</label>
        <input data-testid="admin-email" type="email" className={input} value={email} onChange={(e) => setEmail(e.target.value)} />
        <label className="block text-sm text-[#78716C] mb-2 mt-5">Password</label>
        <input data-testid="admin-password" type="password" className={input} value={password} onChange={(e) => setPassword(e.target.value)} />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="w-full mt-8 bg-[#1A1713] text-[#FDFBF7] py-4 text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-[#1A1713] transition-colors disabled:opacity-50"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [readIds, setReadIds] = useState(
    () => new Set(JSON.parse(localStorage.getItem("aurelume_admin_read") || "[]"))
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, c, s] = await Promise.all([
        axios.get(`${API}/admin/enquiries`, authHeaders(token)),
        axios.get(`${API}/admin/concept-requests`, authHeaders(token)),
        axios.get(`${API}/admin/welcome-sources`, authHeaders(token)),
      ]);
      setEnquiries(e.data);
      setConcepts(c.data);
      setSources(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        onLogout();
      } else {
        toast.error("Failed to load data");
      }
    }
    setLoading(false);
  }, [token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const fmt = (iso) => new Date(iso).toLocaleString();

  const inRange = (iso) => {
    const t = new Date(iso).getTime();
    if (dateFrom && t < new Date(`${dateFrom}T00:00:00`).getTime()) return false;
    if (dateTo && t > new Date(`${dateTo}T23:59:59`).getTime()) return false;
    return true;
  };

  const fEnq = enquiries.filter((e) => inRange(e.created_at));
  const fCon = concepts.filter((c) => inRange(c.created_at));
  const fSrc = sources.filter((s) => inRange(s.created_at));

  const newEnq = enquiries.filter((e) => !readIds.has(e.id)).length;
  const newCon = concepts.filter((c) => !readIds.has(c.id)).length;
  const isNew = (id) => !readIds.has(id);

  const byStatus = (x) => statusFilter === "All" || (x.status || "New") === statusFilter;
  const q = query.trim().toLowerCase();
  const matchEnq = (e) => !q || [e.name, e.email, e.phone, e.message, e.service].some((v) => (v || "").toLowerCase().includes(q));
  const matchCon = (c) => !q || [c.business, c.email, c.industry, c.understanding, c.website].some((v) => (v || "").toLowerCase().includes(q));
  const activeLeads = tab === "enquiries" ? fEnq : tab === "concepts" ? fCon : [];
  const shownEnq = fEnq.filter(byStatus).filter(matchEnq);
  const shownCon = fCon.filter(byStatus).filter(matchCon);

  const persistRead = (set) => {
    setReadIds(set);
    localStorage.setItem("aurelume_admin_read", JSON.stringify([...set]));
  };
  const markAllRead = () => {
    const next = new Set([...readIds, ...enquiries.map((e) => e.id), ...concepts.map((c) => c.id)]);
    persistRead(next);
    toast.success("All leads marked as read.");
  };
  const clearDates = () => {
    setDateFrom("");
    setDateTo("");
  };

  const iso = (d) => d.toISOString().slice(0, 10);
  const setLast7 = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 6);
    setDateFrom(iso(from));
    setDateTo(iso(to));
  };
  const setThisMonth = () => {
    const now = new Date();
    setDateFrom(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
    setDateTo(iso(now));
  };

  const STATUSES = ["New", "Contacted", "Won", "Archived"];
  const STATUS_COLORS = { New: "#C5A059", Contacted: "#4E86C6", Won: "#6C7A5C", Archived: "#78716C" };

  const updateStatus = async (kind, id, status) => {
    const path = kind === "enquiry" ? "enquiries" : "concept-requests";
    try {
      await axios.patch(`${API}/admin/${path}/${id}/status`, { status }, authHeaders(token));
      const setter = kind === "enquiry" ? setEnquiries : setConcepts;
      setter((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked ${status}`);
    } catch (err) {
      toast.error("Could not update status");
    }
  };

  const StatusControl = ({ kind, item }) => (
    <div className="flex gap-2 mt-4 flex-wrap" data-testid={`status-control-${item.id}`}>
      {STATUSES.map((s) => {
        const active = (item.status || "New") === s;
        return (
          <button
            key={s}
            onClick={() => updateStatus(kind, item.id, s)}
            data-testid={`status-${s.toLowerCase()}-${item.id}`}
            className="text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors"
            style={active ? { background: STATUS_COLORS[s], color: "#fff", borderColor: STATUS_COLORS[s] } : { borderColor: "#E8E4D9", color: "#78716C" }}
          >
            {s}
          </button>
        );
      })}
    </div>
  );

  const exportCSV = () => {
    const rows = { enquiries: shownEnq, concepts: shownCon, sources: fSrc }[tab];
    if (!rows || rows.length === 0) {
      toast.error("Nothing to export in this tab.");
      return;
    }
    const keys = Object.keys(rows[0]);
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aurelume-${tab}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported.");
  };

  const NewBadge = () => (
    <span data-testid="new-badge" className="text-[9px] uppercase tracking-[0.2em] bg-[#C5A059] text-[#1A1713] px-2 py-1 font-medium">
      New
    </span>
  );

  const TABS = [
    ["enquiries", "Enquiries", fEnq.length, newEnq],
    ["concepts", "Concept Requests", fCon.length, newCon],
    ["sources", "Traffic Sources", fSrc.length, 0],
  ];

  return (
    <div className="min-h-screen bg-[#F3EFE6]" data-testid="admin-dashboard">
      <header className="bg-[#1A1713] text-[#FDFBF7]">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 text-[#C5A059]">
            <Logo size={34} />
            <span className="font-serif text-lg tracking-[0.15em] text-[#FDFBF7]">AURELUME · Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={exportCSV} data-testid="admin-export-csv" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FDFBF7]/70 hover:text-[#FDFBF7]">
              <Download size={15} /> Export CSV
            </button>
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#FDFBF7]/70 hover:text-[#FDFBF7]">
              <RefreshCw size={15} /> Refresh
            </button>
            <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-white/20 px-4 py-2 hover:bg-white/10">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div className="flex gap-2 flex-wrap">
            {TABS.map(([key, label, count, newCount]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                data-testid={`admin-tab-${key}`}
                className={`relative px-5 py-3 text-xs uppercase tracking-[0.18em] border transition-colors ${
                  tab === key ? "bg-[#1A1713] text-[#FDFBF7] border-[#1A1713]" : "border-[#E8E4D9] text-[#78716C] hover:border-[#C5A059]"
                }`}
              >
                {label} <span className="text-[#C5A059] ml-1">{count}</span>
                {newCount > 0 && (
                  <span data-testid={`admin-newcount-${key}`} className="ml-2 inline-flex items-center justify-center text-[10px] bg-[#C5A059] text-[#1A1713] rounded-full px-2 py-0.5 font-semibold">
                    {newCount} new
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex gap-2">
              <button onClick={setLast7} data-testid="range-7days" className="text-[10px] uppercase tracking-[0.18em] border border-[#E8E4D9] px-3 py-2 hover:border-[#C5A059] text-[#78716C]">Last 7 days</button>
              <button onClick={setThisMonth} data-testid="range-month" className="text-[10px] uppercase tracking-[0.18em] border border-[#E8E4D9] px-3 py-2 hover:border-[#C5A059] text-[#78716C]">This month</button>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#78716C] mb-1">From</label>
              <input type="date" data-testid="filter-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-white border border-[#E8E4D9] px-3 py-2 text-sm outline-none focus:border-[#C5A059]" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.2em] text-[#78716C] mb-1">To</label>
              <input type="date" data-testid="filter-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-white border border-[#E8E4D9] px-3 py-2 text-sm outline-none focus:border-[#C5A059]" />
            </div>
            {(dateFrom || dateTo) && (
              <button onClick={clearDates} data-testid="filter-clear" className="text-xs uppercase tracking-[0.18em] text-[#78716C] underline py-2">Clear</button>
            )}
            <button onClick={markAllRead} data-testid="admin-mark-read" className="border border-[#1A1713] px-4 py-2 text-xs uppercase tracking-[0.18em] hover:bg-[#1A1713] hover:text-[#FDFBF7] transition-colors">
              Mark all read
            </button>
          </div>
        </div>

        {!loading && (tab === "enquiries" || tab === "concepts") && (
          <div className="relative mb-6 max-w-md" data-testid="admin-search-wrap">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#78716C]" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email or business…"
              className="w-full bg-white border border-[#E8E4D9] pl-11 pr-4 py-3 text-sm outline-none focus:border-[#C5A059] transition-colors"
            />
          </div>
        )}

        {!loading && (tab === "enquiries" || tab === "concepts") && (
          <div className="mb-8" data-testid="pipeline-tally">
            <div className="flex flex-wrap gap-3">
              {["All", ...STATUSES].map((s) => {
                const count = s === "All" ? activeLeads.length : activeLeads.filter((x) => (x.status || "New") === s).length;
                const active = statusFilter === s;
                const color = s === "All" ? "#1A1713" : STATUS_COLORS[s];
                return (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    data-testid={`statusfilter-${s.toLowerCase()}`}
                    className="flex items-center gap-2 border px-4 py-3 transition-colors"
                    style={active ? { borderColor: color, background: `${color}14` } : { borderColor: "#E8E4D9" }}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: color }} />
                    <span className="text-xs uppercase tracking-[0.15em] text-[#1A1713]">{s}</span>
                    <span className="font-serif text-lg leading-none" style={{ color }}>{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {loading && <p className="text-[#78716C]">Loading…</p>}

        {!loading && tab === "enquiries" && (
          <div className="space-y-4" data-testid="admin-enquiries-list">
            {shownEnq.length === 0 && <p className="text-[#78716C]">No enquiries match this filter.</p>}
            {shownEnq.map((e) => (
              <div key={e.id} className={`bg-[#FDFBF7] border p-6 ${isNew(e.id) ? "border-[#C5A059] border-l-4" : "border-[#E8E4D9]"}`}>
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div>
                    <h3 className="font-serif text-2xl flex items-center gap-3">{e.name} {isNew(e.id) && <NewBadge />}</h3>
                    <div className="flex gap-4 text-sm text-[#78716C] mt-1 flex-wrap">
                      <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 hover:text-[#1A1713]"><Mail size={13} />{e.email}</a>
                      {e.phone && <span className="inline-flex items-center gap-1"><Phone size={13} />{e.phone}</span>}
                    </div>
                  </div>
                  <span className="text-xs text-[#78716C]">{fmt(e.created_at)}</span>
                </div>
                <div className="flex gap-6 mt-3 text-xs uppercase tracking-wide text-[#8a6d2f]">
                  {e.service && <span>Service: {e.service}</span>}
                  {e.budget && <span>Budget: {e.budget}</span>}
                </div>
                <p className="text-[#1A1713]/80 mt-3 leading-relaxed">{e.message}</p>
                <StatusControl kind="enquiry" item={e} />
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "concepts" && (
          <div className="space-y-4" data-testid="admin-concepts-list">
            {shownCon.length === 0 && <p className="text-[#78716C]">No concept requests match this filter.</p>}
            {shownCon.map((c) => (
              <div key={c.id} className={`bg-[#FDFBF7] border p-6 ${isNew(c.id) ? "border-[#C5A059] border-l-4" : "border-[#E8E4D9]"}`}>
                <div className="flex justify-between items-start gap-4 flex-wrap">
                  <div>
                    <h3 className="font-serif text-2xl flex items-center gap-3">{c.business} {isNew(c.id) && <NewBadge />}</h3>
                    <div className="flex gap-4 text-sm text-[#78716C] mt-1 flex-wrap">
                      <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 hover:text-[#1A1713]"><Mail size={13} />{c.email}</a>
                      {c.industry && <span className="uppercase tracking-wide text-[#8a6d2f] text-xs">{c.industry}</span>}
                      {c.website && <span>{c.website}</span>}
                    </div>
                  </div>
                  <span className="text-xs text-[#78716C]">{fmt(c.created_at)}</span>
                </div>
                {c.understanding && <p className="text-[#1A1713]/80 mt-3 leading-relaxed italic">“{c.understanding}”</p>}
                <StatusControl kind="concept" item={c} />
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "sources" && (
          <div className="bg-[#FDFBF7] border border-[#E8E4D9]" data-testid="admin-sources-list">
            {fSrc.length === 0 && <p className="text-[#78716C] p-6">No data in this period.</p>}
            {fSrc.map((s) => (
              <div key={s.id} className="flex justify-between items-center px-6 py-4 border-b border-[#E8E4D9] last:border-0">
                <div>
                  <span className="font-medium">{s.source}</span>
                  {s.detail && <span className="text-sm text-[#78716C] ml-3">{s.detail}</span>}
                </div>
                <span className="text-xs text-[#78716C]">{fmt(s.created_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
